import { useMemo, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'

import LoadingSpinner from './LoadingSpinner'
import { shouldShowSelectRolePage } from 'util/unified'
import { SelectUserRole } from './SelectUserRole'

import Login from 'pages/authentication/Login'
import {
	selectIsAuthCheckLoading,
	selectUser,
	selectIsAuthCheckUsingTokenLoading,
	selectContext,
	selectIsScratchUserLoggingIn
} from 'selectors/auth'
import { useCurrentPractice } from 'contexts/PracticeContext'

type AuthGuardProps = {
	children?: React.ReactNode
}

const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
	const location = useLocation()
	const [requestedLocation, setRequestedLocation] = useState<string | null>(null)
	const isAuthCheckLoading = useSelector(selectIsAuthCheckLoading)
	const isAuthCheckUsingTokenLoading = useSelector(selectIsAuthCheckUsingTokenLoading)
	const isScratchUserLoggingIn = useSelector(selectIsScratchUserLoggingIn)
	const { currentPracticeObject, showPayments } = useCurrentPractice()
	const context = useSelector(selectContext)
	const user = useSelector(selectUser)

	const showSelectRolePage = useMemo(
		() => shouldShowSelectRolePage(context, currentPracticeObject, showPayments),
		[context, currentPracticeObject, showPayments]
	)

	if (isAuthCheckLoading || isAuthCheckUsingTokenLoading || isScratchUserLoggingIn) {
		return <LoadingSpinner />
	}

	if (!user) {
		if (location.pathname !== requestedLocation) {
			setRequestedLocation(location.pathname)
		}

		return <Login />
	}

	// This is done so that in case the route changes by any chance through other
	// means between the moment of request and the render we navigate to the initially
	// requested route.
	if (requestedLocation && location.pathname !== requestedLocation) {
		setRequestedLocation(null)
		return <Navigate to={requestedLocation} />
	}

	if (showSelectRolePage) {
		return <SelectUserRole user={user} />
	}

	return <>{children}</>
}

export default AuthGuard
